import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ContentTextDTO, ContentVersionDTO } from '@today/types'
import type { DiffViewProps, VersionsPanelViewProps } from '@today/ui-domain'
import { useState } from 'react'
import { contentService } from '@/services/content.service'
import { day } from './content-ui'
import { newVersionBody, newVersionErrors } from './localization-rules'
import { statusLabel } from './text-list-rules'
import { comparableVersions, inlineRows, sideBySideRows } from './track-rules'
import { contentKeys, useContentT, useContentViewer, useNow } from './useContentQueries'

export interface CompareTarget {
	locale: string
	from: number
	to: number
}

type NewVersionState = { basedOn: number; summary: string; error?: string; formError: string | null }

/** "New version from vN": dialog, schema check, POST, then select the new version. */
function useNewVersion(text: ContentTextDTO, versions: readonly ContentVersionDTO[], onCreated: (n: number) => void) {
	const { t, errorMessage } = useContentT()
	const client = useQueryClient()
	const [state, setState] = useState<NewVersionState | null>(null)
	const create = useMutation({
		mutationFn: (body: Record<string, unknown>) => contentService.createVersion(text.id, body),
		onSuccess: async (created) => {
			setState(null)
			await client.invalidateQueries({ queryKey: contentKeys.versions(text.id) })
			await client.invalidateQueries({ queryKey: contentKeys.text(text.id) })
			await client.invalidateQueries({ queryKey: contentKeys.texts })
			onCreated(created.versionNumber)
		},
		onError: (error) => setState((s) => s && { ...s, formError: errorMessage(error) }),
	})
	const confirm = () => {
		if (!state) return
		const base = versions.find((v) => v.versionNumber === state.basedOn)
		const body = newVersionBody(base?.id ?? '', state.summary)
		const key = newVersionErrors(body).changeSummary
		setState({ ...state, error: key ? t(key) : undefined, formError: null })
		if (!key) create.mutate(body)
	}
	return {
		state,
		isBusy: create.isPending,
		open: (basedOn: number) => setState({ basedOn, summary: '', formError: null }),
		setSummary: (summary: string) => setState((s) => s && { ...s, summary }),
		confirm,
		cancel: () => setState(null),
	}
}

/** The diff of `to` against `from` for one language (TO-420 content-admin-ui REQ-007). */
function useDiff(versions: readonly ContentVersionDTO[], target: CompareTarget | null, close: () => void) {
	const { t, errorMessage } = useContentT()
	const [mode, setMode] = useState<'inline' | 'side'>('inline')
	const options = target ? comparableVersions(versions, target.locale) : []
	const from = options.find((o) => o.versionNumber === target?.from)?.localizationId
	const to = options.find((o) => o.versionNumber === target?.to)?.localizationId
	const diff = useQuery({
		queryKey: contentKeys.diff(to ?? '', from ?? ''),
		queryFn: () => contentService.diff(to as string, from as string),
		enabled: Boolean(from && to),
	})
	if (!target) return null
	const body = diff.data?.body ?? []
	const props: DiffViewProps = {
		t,
		fromLabel: `v${target.from} · ${target.locale}`,
		toLabel: `v${target.to} · ${target.locale}`,
		mode,
		title: diff.data?.title.changed ? { before: diff.data.title.before, after: diff.data.title.after } : null,
		inline: inlineRows(body),
		side: sideBySideRows(body),
		error: diff.error ? errorMessage(diff.error) : null,
		onModeChange: setMode,
		onClose: close,
	}
	return props
}

/** The compare picker's defaults: the selected language, the previous version holding it, the selected one. */
const defaultPicker = (versions: readonly ContentVersionDTO[], selected: { versionNumber: number; locale: string }) => {
	const options = comparableVersions(versions, selected.locale)
	return {
		locale: selected.locale,
		from: String(options.find((o) => o.versionNumber < selected.versionNumber)?.versionNumber ?? ''),
		to: String(selected.versionNumber),
	}
}

/** Version rows for the panel, dates as `YYYY-MM-DD`. */
const versionRows = (versions: readonly ContentVersionDTO[], now: Date) =>
	versions.map((v) => ({
		id: v.id,
		versionNumber: v.versionNumber,
		changeSummary: v.changeSummary,
		createdAt: day(v.createdAt),
		localizations: v.localizations.map((l) => ({
			locale: l.locale,
			label: statusLabel(l, now),
			validFrom: l.validFrom && day(l.validFrom),
			validTo: l.validTo && day(l.validTo),
		})),
	}))

/**
 * The versions panel, its compare picker and the diff (TO-420 content-admin-ui
 * REQ-004/REQ-007). `initialCompare` opens a diff on load (the inbox link).
 */
export function useVersionsPanel(args: {
	text: ContentTextDTO
	versions: readonly ContentVersionDTO[]
	selected: { versionNumber: number; locale: string }
	initialCompare: CompareTarget | null
	onSelect: (versionNumber: number) => void
}): { panel: VersionsPanelViewProps; diff: DiffViewProps | null; openNewVersion: (basedOn: number) => void } {
	const { text, versions, selected } = args
	const { t } = useContentT()
	const { caps } = useContentViewer()
	const now = useNow()
	const newVersion = useNewVersion(text, versions, args.onSelect)
	const [picker, setPicker] = useState<{ locale: string; from: string; to: string } | null>(null)
	const [target, setTarget] = useState<CompareTarget | null>(args.initialCompare)
	const diff = useDiff(versions, target, () => setTarget(null))
	// Only languages that at least two versions hold can be compared (SCN-007).
	const locales = [...new Set(versions.flatMap((v) => v.localizations.map((l) => l.locale)))]
		.filter((l) => comparableVersions(versions, l).length >= 2)
		.sort()
	const openPicker = () => setPicker(defaultPicker(versions, selected))
	const panel: VersionsPanelViewProps = {
		t,
		versions: versionRows(versions, now),
		selected: selected.versionNumber,
		newVersion: newVersion.state,
		compare: picker && {
			...picker,
			locales,
			options: comparableVersions(versions, picker.locale).map((o) => o.versionNumber),
		},
		isBusy: newVersion.isBusy,
		onSelect: args.onSelect,
		onNewVersion: caps.write ? newVersion.open : undefined,
		onSummaryChange: newVersion.setSummary,
		onConfirmNewVersion: newVersion.confirm,
		onCancelNewVersion: newVersion.cancel,
		onOpenCompare: openPicker,
		onCompareChange: (field, value) =>
			setPicker((p) => p && (field === 'locale' ? { locale: value, from: '', to: '' } : { ...p, [field]: value })),
		onRunCompare: () => {
			if (!picker) return
			const [a, b] = [Number(picker.from), Number(picker.to)]
			setTarget({ locale: picker.locale, from: Math.min(a, b), to: Math.max(a, b) })
		},
		onCloseCompare: () => setPicker(null),
	}
	return { panel, diff, openNewVersion: newVersion.open }
}
